'use client'

/**
 * Header-Quick-Icons (R102, 2026-05-31, P15 + P19)
 *
 * Zwei kleine Icons oben im Header:
 *   - Alarmglocke mit Badge = Gesamtzahl ungelesener Nachrichten
 *     Klick → /app/messages
 *   - KI-Bubble (Cirra public / Freeglidy logged-in)
 *     Klick → öffnet das passende Bot-Widget via CustomEvent
 *
 * Sichtbar nur wenn User eingeloggt ist.
 */

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { Bell, MessagesSquare } from 'lucide-react'

export function HeaderQuickIcons() {
  const { status } = useSession()
  const [unread, setUnread] = useState(0)

  useEffect(() => {
    if (status !== 'authenticated') return
    let cancelled = false
    const load = () => {
      fetch('/api/messages/unread-count', { credentials: 'same-origin' })
        .then(r => (r.ok ? r.json() : null))
        .then(j => {
          if (!cancelled && j && typeof j.count === 'number') setUnread(j.count)
        })
        .catch(() => {})
    }
    load()
    const id = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [status])

  if (status !== 'authenticated') {
    // Nur Chatbot-Icon für public — öffnet Cirra
    return (
      <button
        type="button"
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('cirra:open', { detail: {} }))
          }
        }}
        title="Cirra — KI-Sekretärin der FREE GLIDE LEAGUE"
        aria-label="Cirra öffnen"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
        style={{
          background: 'color-mix(in oklch, var(--accent, #f2c94e) 12%, transparent)',
          border: '1px solid color-mix(in oklch, var(--accent, #f2c94e) 30%, transparent)',
          color: 'var(--accent, #f2c94e)',
        }}
      >
        <MessagesSquare className="w-4 h-4" />
        <span
          className="absolute -top-1 -right-1 text-[8px] font-black px-1 rounded-sm leading-tight"
          style={{ background: 'var(--accent, #f2c94e)', color: '#000', letterSpacing: '0.02em' }}
        >
          KI
        </span>
      </button>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {/* Alarmglocke — Nachrichten */}
      <Link
        href="/app/messages"
        title={unread > 0 ? `${unread} ungelesene Nachricht${unread === 1 ? '' : 'en'}` : 'Nachrichten'}
        aria-label="Nachrichten"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
        style={{
          background: unread > 0
            ? 'color-mix(in oklch, var(--accent, #f2c94e) 18%, transparent)'
            : 'color-mix(in oklch, var(--accent, #f2c94e) 8%, transparent)',
          border: '1px solid color-mix(in oklch, var(--accent, #f2c94e) 30%, transparent)',
          color: 'var(--accent, #f2c94e)',
        }}
      >
        <Bell className={`w-4 h-4 ${unread > 0 ? 'fgl-bell-shake' : ''}`} />
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black flex items-center justify-center"
            style={{ background: '#ef4444', color: 'white', boxShadow: '0 0 0 2px var(--bg-primary, #000)' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Link>

      {/* Freeglidy-Bubble — KI-Begleiter */}
      <button
        type="button"
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('cirra:open', { detail: {} }))
          }
        }}
        title="Freeglidy — dein FGL-KI-Begleiter"
        aria-label="Freeglidy öffnen"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
        style={{
          background: 'color-mix(in oklch, var(--accent, #f2c94e) 12%, transparent)',
          border: '1px solid color-mix(in oklch, var(--accent, #f2c94e) 30%, transparent)',
          color: 'var(--accent, #f2c94e)',
        }}
      >
        <MessagesSquare className="w-4 h-4" />
        <span
          className="absolute -top-1 -right-1 text-[8px] font-black px-1 rounded-sm leading-tight"
          style={{ background: 'var(--accent, #f2c94e)', color: '#000', letterSpacing: '0.02em' }}
        >
          KI
        </span>
      </button>

      <style jsx global>{`
        @keyframes fgl-bell-shake {
          0%, 100% { transform: rotate(0deg); }
          20%      { transform: rotate(-12deg); }
          40%      { transform: rotate(10deg); }
          60%      { transform: rotate(-6deg); }
          80%      { transform: rotate(4deg); }
        }
        .fgl-bell-shake { animation: fgl-bell-shake 1.4s ease-in-out infinite; transform-origin: center top; }
      `}</style>
    </div>
  )
}
